import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Form,
  Grid,
  Icon,
  Popup,
  Segment,
  Select
} from 'semantic-ui-react'
import { format } from 'date-fns'
// Local
import { formatDob } from '../../common/formatUtils'
import {
  API_URL,
  ENCOUNTER,
  FORMAT,
  LIST_PAGE_SIZE,
  LIST_MAX_PAGES,
  LOCK,
  PATIENT,
  REVIEW,
  WORKLIST_VIEW,
  shouldRenderReskin2025
} from '../../../constant'
import { api } from '../../../action/api'
import { searchGenerator } from '../../../action/search'
import { updateUserWorklistFilters } from '../../../action/user'
import SubHeader from '../../common/SubHeader'
import Filter from '../../common/filter'
import Table from '../../common/Table'
import { patientMrn } from '../../common/patientUtils'
import {
  showVbcDisplay,
  encounterConditionDisplayNlpInsights
} from '../../common/currentUserUtils'
import ReviewStatusIcon from '../common/reviewStatusIcon'
import CdiEncounterStatusIcon from './cdiEncounterStatusIcon'
import { useCdiRestricted } from '../../common/roleUtils'
import ReveleerControlButton from '../../common/ReveleerControlButton'
import FilterIcon from '../../icons/Filter'
import { User } from '../../authenticated/types'
import { RootState } from '../../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>

type WorklistFilters = {
  encounterSort?: string
  view?: string
  start?: Date | null
  birthDate?: Date | null
  firstName?: string | null
  lastName?: string | null
  patientOrgId?: string | null
  appointmentType?: string[] | null
  practitionerId?: string | null
  patientRegion?: string | null
  potentialConditionCount?: string | null
  nlpConditionCount?: string | null
  specialty?: string | null
  vbcProgram?: string | null
  mrn?: string | null
  defaultPatientRegion?: string
  defaultPatientOffshoreFilter?: string
}

type CurrentUser = User & {
  tenantId?: string
  worklistFilters: WorklistFilters
}

type Patient = {
  firstName: string
  lastName: string
  birthDate: string
  vbcProgram?: string
}

type Practitioner = {
  firstName: string
  lastName: string
}

type Review = {
  reviewCode: string
}

type EncounterRecord = {
  id: string
  start: string
  appointmentType: string[]
  openHccCount?: number
  lastReviewDate?: string
  patient: Patient
  practitioner: Practitioner
  review: Review
}

type QueryFilters = {
  start?: Date | null
  birthDate?: Date | null
  firstName?: string | null
  lastName?: string | null
  patientOrgId?: string | null
  appointmentType?: string[] | null
  practitionerId?: string | null
  patientRegion?: string | null
  potentialConditionCount?: string | null
  nlpConditionCount?: string | null
  specialty?: string | null
  vbcProgram?: string | null
  mrn?: string | null
  reviewCode?: string[]
  encounterStatus?: string[]
  lockStatus?: string | null
  patientStatus?: string[]
  reviewQueryStatus?: string | null
  onOffShore?: string
}

type CdiWorklistOwnProps = {
  onSelect?: (id: string, event: React.MouseEvent) => void
}

type CdiWorklistReduxProps = {
  currentUser: CurrentUser
  updateUserWorklistFilters: (user: CurrentUser, filters: Partial<WorklistFilters>) => void
}

type CdiWorklistProps = CdiWorklistOwnProps & CdiWorklistReduxProps

const worklistViewOptions = [
  {
    key: WORKLIST_VIEW.CDI.WORKLIST,
    text: 'Worklist',
    value: WORKLIST_VIEW.CDI.WORKLIST
  },
  {
    key: WORKLIST_VIEW.CDI.OPEN,
    text: 'Open',
    value: WORKLIST_VIEW.CDI.OPEN
  },
  {
    key: WORKLIST_VIEW.CDI.REVIEW_REQUEST,
    text: 'CDI Review Request',
    value: WORKLIST_VIEW.CDI.REVIEW_REQUEST
  },
  {
    key: WORKLIST_VIEW.CDI.READY_FOR_CDI_QA,
    text: 'Ready for CDI QA',
    value: WORKLIST_VIEW.CDI.READY_FOR_CDI_QA
  },
  {
    key: WORKLIST_VIEW.CDI.READY_FOR_PHYSICIAN,
    text: 'Ready for Physician',
    value: WORKLIST_VIEW.CDI.READY_FOR_PHYSICIAN
  },
  {
    key: WORKLIST_VIEW.CDI.LOCKED,
    text: 'Locked',
    value: WORKLIST_VIEW.CDI.LOCKED
  },
  {
    key: WORKLIST_VIEW.CDI.CANCELLED,
    text: 'Cancelled',
    value: WORKLIST_VIEW.CDI.CANCELLED
  }
]

/**
 * Provides a worklist view of encounters for the CDI user role
 *
 * @param onSelect Callback function to execute when an encounter is selected
 *        arguments: Encounter ID
 * @param currentUser The current user
 */
function CdiWorklist(
  props: CdiWorklistProps
): JSX.Element {
  const {
    onSelect,
    currentUser,
    updateUserWorklistFilters: updateFilters
  } = props

  const [
    encounters,
    setEncounters
  ] = useState<EncounterRecord[]>([])
  const [
    worklistView,
    setWorklistView
  ] = useState<string>(WORKLIST_VIEW.CDI.WORKLIST)
  const [
    pagesDisplayed,
    setPagesDisplayed
  ] = useState<number>(0)
  const [
    encounterSort,
    setEncounterSort
  ] = useState<string>('start asc')
  const [
    isFilterVisible,
    setIsFilterVisible
  ] = useState<boolean>(true)
  const [
    queryFilters,
    setQueryFilters
  ] = useState<QueryFilters>({})

  const fetchEncounters = (
    view: string,
    query: QueryFilters,
    sort: string,
    pages: number
  ) => {
    const searchQuery: QueryFilters = query || {}
    let encounterStatus: string[] = [ ENCOUNTER.STATUS.PLANNED ]
    const patientStatus: string[] = [ PATIENT.STATUS.ACTIVE ]
    let reviewCode: string[] = [
      REVIEW.CODE.OPEN,
      REVIEW.CODE.READY_FOR_CDI_QA
    ]
    let reviewQueryStatus: string | null = null
    let lockStatus: string | null = null

    switch (view) {
      case WORKLIST_VIEW.CDI.CANCELLED:
        reviewCode = [
          REVIEW.CODE.OPEN,
          REVIEW.CODE.READY_FOR_CDI_QA,
          REVIEW.CODE.READY_FOR_PHYSICIAN
        ]
        encounterStatus = [ ENCOUNTER.STATUS.CANCELLED ]
        break
      case WORKLIST_VIEW.CDI.REVIEW_REQUEST:
        reviewQueryStatus = REVIEW.QUERY_STATUS.REVIEW_QUERY_OUTSTANDING
        break
      case WORKLIST_VIEW.CDI.LOCKED:
        lockStatus = LOCK.STATUS.ACTIVE
        encounterStatus = [ ENCOUNTER.STATUS.PLANNED ]
        break
      case WORKLIST_VIEW.CDI.READY_FOR_CDI_QA:
        reviewCode = [ REVIEW.CODE.READY_FOR_CDI_QA ]
        encounterStatus = [ ENCOUNTER.STATUS.PLANNED ]
        break
      case WORKLIST_VIEW.CDI.READY_FOR_PHYSICIAN:
        reviewCode = [ REVIEW.CODE.READY_FOR_PHYSICIAN ]
        encounterStatus = [ ENCOUNTER.STATUS.PLANNED ]
        break
      case WORKLIST_VIEW.CDI.OPEN:
        reviewCode = [ REVIEW.CODE.OPEN ]
        encounterStatus = [ ENCOUNTER.STATUS.PLANNED ]
        break
      default:
        reviewCode = [
          REVIEW.CODE.OPEN,
          REVIEW.CODE.READY_FOR_CDI_QA
        ]
        encounterStatus = [ ENCOUNTER.STATUS.PLANNED ]
    }

    if (!searchQuery.patientRegion) {
      const defaultRegions = currentUser.worklistFilters.defaultPatientRegion
      if (defaultRegions) {
        searchQuery.patientRegion = defaultRegions
      }
    }
    if (!searchQuery.onOffShore) {
      const offshoreFilter = currentUser.worklistFilters.defaultPatientOffshoreFilter
      if (offshoreFilter) {
        searchQuery.onOffShore = offshoreFilter
      }
    }

    searchQuery.reviewCode = reviewCode
    searchQuery.encounterStatus = encounterStatus
    searchQuery.lockStatus = lockStatus
    searchQuery.patientStatus = patientStatus
    searchQuery.reviewQueryStatus = reviewQueryStatus
    searchQuery.appointmentType = Array.isArray(searchQuery.appointmentType) && searchQuery.appointmentType.length == 0 ? null : searchQuery.appointmentType

    const effectiveRole = useCdiRestricted(currentUser)
    const url = API_URL.ENCOUNTER_SEARCH_WITH_ROLE(effectiveRole)

    const pagesAfterFetch = pages + 1
    const limit = pagesAfterFetch * LIST_PAGE_SIZE
    const params = searchGenerator(
      searchQuery,
      limit,
      0,
      sort,
      undefined
    )

    api.get(url, { params }).then(({ data }) => {
      setEncounters(data as EncounterRecord[])
      setWorklistView(view)
      setPagesDisplayed(pagesAfterFetch)
      setEncounterSort(sort)
      setQueryFilters(query)
    }).catch(() => {
      // Error handled by api.defaultErrorHandler
    })
  }

  useEffect(() => {
    const userFilters = currentUser.worklistFilters
    const initialSort = userFilters.encounterSort
      ? userFilters.encounterSort
      : 'start asc'
    const initialView = userFilters.view
      ? userFilters.view
      : WORKLIST_VIEW.CDI.WORKLIST
    const query: QueryFilters = {
      start: userFilters.start
        ? new Date(userFilters.start)
        : null,
      birthDate: userFilters.birthDate
        ? new Date(userFilters.birthDate)
        : null,
      firstName: userFilters.firstName
        ? userFilters.firstName
        : null,
      lastName: userFilters.lastName
        ? userFilters.lastName
        : null,
      patientOrgId: userFilters.patientOrgId
        ? userFilters.patientOrgId
        : null,
      appointmentType: userFilters.appointmentType
        ? userFilters.appointmentType
        : [],
      practitionerId: userFilters.practitionerId
        ? userFilters.practitionerId
        : null,
      patientRegion: userFilters.patientRegion
        ? userFilters.patientRegion
        : null,
      potentialConditionCount: userFilters.potentialConditionCount
        ? userFilters.potentialConditionCount
        : null,
      nlpConditionCount: userFilters.nlpConditionCount
        ? userFilters.nlpConditionCount
        : null,
      specialty: userFilters.specialty
        ? userFilters.specialty
        : null,
      vbcProgram: userFilters.vbcProgram
        ? userFilters.vbcProgram
        : null,
      mrn: userFilters.mrn
        ? userFilters.mrn
        : null
    }

    setEncounterSort(initialSort)
    fetchEncounters(
      initialView,
      query,
      initialSort,
      0
    )
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onWorklistViewSelect = (
    event: React.SyntheticEvent,
    data: { value: string }
  ) => {
    const view = data.value
    const userFilters = currentUser.worklistFilters
    userFilters.view = view
    updateFilters(
      currentUser,
      userFilters
    )
    fetchEncounters(
      view,
      queryFilters,
      encounterSort,
      0
    )
    setPagesDisplayed(0)
  }

  const getFilterMetadata = () => {
    const hasVbcFilter = showVbcDisplay(currentUser)
    const hasNlpFilter = encounterConditionDisplayNlpInsights(currentUser)
    const metadata = [
      [
        {
          placeholder: 'Patient MRN',
          name: 'mrn',
          width: 6,
          like: false
        },
        {
          placeholder: 'First Name',
          name: 'firstName',
          width: 5,
          like: true
        },
        {
          placeholder: 'Last Name',
          name: 'lastName',
          width: 5,
          like: true
        }
      ],
      [
        {
          placeholder: 'Appointment Date',
          name: 'start',
          width: 6,
          date: true
        },
        {
          placeholder: 'Date of Birth',
          name: 'birthDate',
          width: 5,
          date: true
        },
        {
          placeholder: 'Practice',
          name: 'patientOrgId',
          width: 5,
          organizationSelect: true
        }
      ],
      [
        {
          placeholder: 'Visit Type',
          name: 'appointmentType',
          width: 6,
          appointmentTypeSelect: true
        },
        {
          placeholder: 'Practitioner',
          name: 'practitionerId',
          width: 5,
          practitionerSelect: true
        },
        {
          placeholder: 'Region',
          name: 'patientRegion',
          width: 5,
          patientRegionSelect: true
        }
      ],
      [
        {
          placeholder: 'Potential Opportunities',
          name: 'potentialConditionCount',
          width: 6,
          potentialCountSelect: true
        },
        {
          placeholder: 'Specialty',
          name: 'specialty',
          width: 5,
          specialtySelect: true
        }
      ],
      [] as any[]
    ]

    if (hasVbcFilter) {
      metadata[3].push({
        placeholder: 'VBC Program',
        name: 'vbcProgram',
        width: 5,
        vbcProgramSelect: true
      })
    }

    if (hasNlpFilter) {
      metadata[4].push({
        placeholder: 'NLP Encounters Only',
        name: 'nlpConditionCount',
        width: 6,
        nlpConditionSelect: true
      })
    }

    return metadata
  }

  const handleEncounterSort = (sort: string) => {
    updateFilters(currentUser, { encounterSort: sort })
    fetchEncounters(
      worklistView,
      queryFilters,
      sort,
      0
    )
    setPagesDisplayed(0)
  }

  const sortOnHeaderClick = (clickedColumn: string) => () => {
    let sort = encounterSort

    switch (clickedColumn) {
      case 'time':
        sort = sort === 'start asc'
          ? 'start desc'
          : 'start asc'
        break
      case 'appointmentType':
        sort = sort === 'appointmentType asc'
          ? 'appointmentType desc'
          : 'appointmentType asc'
        break
      case 'name':
        sort = sort === 'lastName asc'
          ? 'lastName desc'
          : 'lastName asc'
        break
      case 'dob':
        sort = sort === 'birthDate asc'
          ? 'birthDate desc'
          : 'birthDate asc'
        break
      case 'vbcProgram':
        sort = sort === 'vbcProgram asc'
          ? 'vbcProgram desc'
          : 'vbcProgram asc'
        break
      case 'openHccCount':
        sort = sort === 'openHccCount desc'
          ? 'openHccCount asc'
          : 'openHccCount desc'
        break
      case 'practitioner':
        sort = sort === 'practitionerLastName asc'
          ? 'practitionerLastName desc'
          : 'practitionerLastName asc'
        break
      case 'status':
        sort = sort === 'statusCode asc'
          ? 'statusCode desc'
          : 'statusCode asc'
        break
      case 'nameMobile':
        sort = sort === 'lastName asc'
          ? 'lastName desc'
          : 'lastName asc'
        break
      case 'lastReviewDate':
        sort = sort === 'lastReviewDate asc'
          ? 'lastReviewDate desc'
          : 'lastReviewDate asc'
        break
      default:
        sort = 'start asc'
    }
    handleEncounterSort(sort)
  }

  const onMore = () => {
    fetchEncounters(
      worklistView,
      queryFilters,
      encounterSort,
      pagesDisplayed
    )
  }

  const selectRecord = (
    id: string,
    event: React.MouseEvent
  ) => {
    if (onSelect && id) {
      onSelect(
        id,
        event
      )
    }
  }

  const toggleFilter = () => {
    setIsFilterVisible(!isFilterVisible)
  }

  const onSubmitFilter = (query: QueryFilters) => {
    updateFilters(
      currentUser,
      {
        start: query.start
          ? new Date(query.start)
          : null,
        birthDate: query.birthDate
          ? new Date(query.birthDate)
          : null,
        firstName: query.firstName
          ? query.firstName.replaceAll('%', '')
          : null,
        lastName: query.lastName
          ? query.lastName.replaceAll('%', '')
          : null,
        patientOrgId: query.patientOrgId
          ? query.patientOrgId
          : null,
        appointmentType: query.appointmentType
          ? query.appointmentType
          : [],
        practitionerId: query.practitionerId
          ? query.practitionerId
          : null,
        patientRegion: query.patientRegion
          ? query.patientRegion
          : null,
        vbcProgram: query.vbcProgram
          ? query.vbcProgram
          : null,
        potentialConditionCount: query.potentialConditionCount
          ? query.potentialConditionCount
          : null,
        nlpConditionCount: query.nlpConditionCount
          ? query.nlpConditionCount
          : null,
        specialty: query.specialty
          ? query.specialty
          : null,
        mrn: query.mrn
          ? query.mrn
          : null,
        view: worklistView
      }
    )
    setPagesDisplayed(0)
    fetchEncounters(
      worklistView,
      query,
      encounterSort,
      0
    )
  }

  const getFilterInitialValues = () => {
    const userFilters = currentUser.worklistFilters
    const initialValues: Record<string, any> = {}
    if (userFilters.start) {
      initialValues.start = format(
        new Date(userFilters.start),
        FORMAT.DATE
      )
    }
    if (userFilters.birthDate) {
      initialValues.birthDate = formatDob(
        new Date(userFilters.birthDate),
        FORMAT.DATE
      )
    }
    initialValues.mrn = userFilters.mrn
      ? userFilters.mrn
      : null
    initialValues.firstName = userFilters.firstName
      ? userFilters.firstName
      : null
    initialValues.lastName = userFilters.lastName
      ? userFilters.lastName
      : null
    initialValues.patientOrgId = userFilters.patientOrgId
      ? userFilters.patientOrgId
      : null
    initialValues.appointmentType = userFilters.appointmentType
      ? userFilters.appointmentType
      : []
    initialValues.practitionerId = userFilters.practitionerId
      ? userFilters.practitionerId
      : null
    initialValues.patientRegion = userFilters.patientRegion
      ? userFilters.patientRegion
      : null
    initialValues.vbcProgram = userFilters.vbcProgram
      ? userFilters.vbcProgram
      : null
    initialValues.potentialConditionCount = userFilters.potentialConditionCount
      ? userFilters.potentialConditionCount
      : null
    initialValues.nlpConditionCount = userFilters.nlpConditionCount
      ? userFilters.nlpConditionCount
      : null
    initialValues.specialty = userFilters.specialty
      ? userFilters.specialty
      : null
    return initialValues
  }

  const generateTableData = (records: EncounterRecord[]) => {
    if (!records || records.length === 0) return []

    const formatPatientName = (
      view: string,
      patient: Patient,
      review: Review
    ) => {
      const name = `${patient.firstName} ${patient.lastName}`
      if (view === WORKLIST_VIEW.CDI.WORKLIST && review
        && review.reviewCode === REVIEW.CODE.OPEN) {
        return <span className="bold">{ name }</span>
      }
      return <span>{ name }</span>
    }

    const formatDate = (
      view: string,
      startDt: string,
      review: Review
    ) => {
      if (view === WORKLIST_VIEW.CDI.WORKLIST && review
        && review.reviewCode === REVIEW.CODE.OPEN) {
        return <span className="bold">{ format(
          new Date(startDt),
          FORMAT.DATE_TIME
        ) }</span>
      }
      return <span>{ format(
        new Date(startDt),
        FORMAT.DATE_TIME
      ) }</span>
    }

    const formatDateShort = (lastReviewDt?: string) => {
      if (lastReviewDt) {
        return <span>{ format(
          new Date(lastReviewDt),
          FORMAT.DATE
        ) }</span>
      }
      return <span>N/A</span>
    }

    return records.map(record => {
      const { patient } = record
      const { practitioner } = record
      const { review } = record
      const practitionerName = `${practitioner.firstName} ${practitioner.lastName}`
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const mrn = patientMrn(patient as any)
      const { vbcProgram } = patient
      const mobileName = `${patient.firstName} ${patient.lastName} ( ${formatDob(
        patient.birthDate,
        FORMAT.DATE
      )} )`

      return {
        id: record.id,
        time: formatDate(
          worklistView,
          record.start,
          review
        ),
        name: formatPatientName(
          worklistView,
          patient,
          review
        ),
        dob: formatDob(
          record.patient.birthDate,
          FORMAT.DATE
        ),
        appointmentType: record.appointmentType,
        practitioner: practitionerName,
        mrn,
        openHccCount: record.openHccCount
          ? record.openHccCount
          : '0',
        vbcProgram,
        nameMobile: mobileName,
        lastReviewDate: formatDateShort(record.lastReviewDate),
        status: <ReviewStatusIcon reviewCode={ record.review.reviewCode as any } />,
        icon: <CdiEncounterStatusIcon encounter={ record as any } />,
        meta: { className: 'cursor-pointer' }
      }
    })
  }

  const getLeftSubHeader = () => {
    const prefix = 'CDI Worklist'
    switch (worklistView) {
      case WORKLIST_VIEW.CDI.CANCELLED:
        return `${prefix} - Cancelled Encounters`
      case WORKLIST_VIEW.CDI.LOCKED:
        return `${prefix} - Locked Encounters`
      case WORKLIST_VIEW.CDI.READY_FOR_CDI_QA:
        return `${prefix} - Ready for CDI QA`
      case WORKLIST_VIEW.CDI.READY_FOR_PHYSICIAN:
        return `${prefix} - Ready for Physician`
      case WORKLIST_VIEW.CDI.OPEN:
        return `${prefix} - Open Encounters`
      default:
        return prefix
    }
  }

  const getRightSubHeader = () => {
    const tooltip = isFilterVisible
      ? 'Show Worklist Filters'
      : 'Hide Worklist Filters'
    return (
      <Popup
        content={ tooltip }
        mouseEnterDelay={ 1000 }
        trigger={
          shouldRenderReskin2025
            ? (
              <ReveleerControlButton
                icon={ FilterIcon }
                id="worklist-filter"
                isDisabled={ false }
                onClick={ toggleFilter }
              />
            )
            : (
              <Icon
                id="worklist-filter"
                name="filter"
                size="large"
                className="pointer"
                onClick={ toggleFilter }
              />
            )
        }
      />
    )
  }

  const renderWorklistTable = () => {
    const tableData = generateTableData(encounters)
    const computerColumns = [
      {
        name: '',
        value: 'icon'
      },
      {
        name: 'Time',
        value: 'time',
        tooltip: 'Encounter Date and Time'
      },
      {
        name: 'Type',
        value: 'appointmentType',
        tooltip: 'Encounter Appointment Type'
      },
      {
        name: 'Name',
        value: 'name',
        tooltip: 'Patient Name'
      },
      {
        name: 'DOB',
        value: 'dob',
        tooltip: 'Patient Date of Birth'
      },
      {
        name: 'Practitioner',
        value: 'practitioner',
        tooltip: 'Encounter Practitioner'
      },
      {
        name: 'Open HCCs',
        value: 'openHccCount',
        tooltip: 'HCCs Not Addressed By Provider'
      },
      {
        name: 'Last Review',
        value: 'lastReviewDate',
        tooltip: 'Last Patient Review Date'
      },
      {
        name: 'Status',
        value: 'status',
        tooltip: 'Review Status'
      }
    ]

    if (showVbcDisplay(currentUser)) {
      computerColumns.splice(
        7,
        0,
        {
          name: 'VBC Program',
          value: 'vbcProgram',
          tooltip: 'Patient VBC Program'
        }
      )
    }

    const shouldDisplayMore = pagesDisplayed > 0 && pagesDisplayed < LIST_MAX_PAGES
    const shouldDisplayMaxRecords = !shouldDisplayMore

    return (
      <span>
        <Table
          key={ worklistView }
          computerColumns={ computerColumns }
          mobileColumns={ [
            {
              value: 'time',
              width: '4'
            },
            {
              value: 'nameMobile',
              width: '12'
            }
          ] }
          data={ tableData }
          onClick={ selectRecord }
          more={ shouldDisplayMore }
          max={ shouldDisplayMaxRecords }
          onMore={ onMore }
          sortOnHeaderClick={ sortOnHeaderClick }
        />
      </span>
    )
  }

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left={ getLeftSubHeader() }
          right={ getRightSubHeader() }
        />
        { !isFilterVisible && React.createElement(Filter as any, {
          submit: onSubmitFilter,
          filters: getFilterMetadata(),
          initialValues: getFilterInitialValues()
        }) }
        <SafeSegment
          attached
          className="no-margin"
        >
          <SafeGrid>
            <Grid.Row style={ {
              paddingBottom: '0rem',
              textAlign: 'right'
            } }>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <Form.Field
                  label="View: "
                  control={ Select }
                  value={ worklistView }
                  options={ worklistViewOptions }
                  onChange={ onWorklistViewSelect }
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                { renderWorklistTable() }
              </Grid.Column>
            </Grid.Row>
          </SafeGrid>
        </SafeSegment>
      </Container>
    </div>
  )
}

const ConnectedCdiWorklist = connect(
  (state: RootState) => ({
    currentUser: state.user as CurrentUser
  }),
  { updateUserWorklistFilters } as any
)(CdiWorklist as any)

export default ConnectedCdiWorklist as React.ComponentType<CdiWorklistOwnProps>
